<template>
  <div class="a2p-main">
    <div class="a2p-eyebrow">{{ eyebrow }}</div>
    <h1 class="a2p-title">Review & submit</h1>
    <p class="a2p-sub">
      One last look before we file your registration with the carriers. Click any
      step in the sidebar to jump back and edit.
    </p>

    <A2PCallout type="info">
      Run <strong>Verify business & representative</strong> before submitting. It checks
      your entity name and rep against public records (BBB, your website) to catch
      mismatches that would otherwise cause automatic rejection.
    </A2PCallout>

    <div class="a2p-review-section">
      <div class="a2p-section-eyebrow">Business</div>
      <div class="a2p-kv"><div class="k">Legal name</div><div class="v">{{ businessName || '—' }}</div></div>
      <div class="a2p-kv"><div class="k">Address</div><div class="v">{{ businessAddress || '—' }}</div></div>
      <div class="a2p-kv"><div class="k">Tax ID / EIN</div><div class="v">{{ ein || '—' }}</div></div>
      <div class="a2p-kv"><div class="k">Business type</div><div class="v">{{ businessType || '—' }}</div></div>
      <div class="a2p-kv"><div class="k">Website</div><div class="v">{{ website || '—' }}</div></div>
    </div>

    <div class="a2p-review-section">
      <div class="a2p-section-eyebrow">Representative</div>
      <div class="a2p-kv"><div class="k">Name</div><div class="v">{{ repName || '—' }}</div></div>
      <div class="a2p-kv"><div class="k">Title</div><div class="v">{{ repTitle || '—' }}</div></div>
      <div class="a2p-kv"><div class="k">Email</div><div class="v">{{ repEmail || '—' }}</div></div>
      <div class="a2p-kv"><div class="k">Phone</div><div class="v">{{ repPhone || '—' }}</div></div>
    </div>

    <div class="a2p-review-section">
      <div class="a2p-section-eyebrow">Brand & messaging</div>
      <div class="a2p-kv">
        <div class="k">Brand description</div>
        <div class="v" style="white-space: pre-wrap">{{ brandDescription || '—' }}</div>
      </div>
      <div class="a2p-kv">
        <div class="k">Sample message</div>
        <div class="v" style="white-space: pre-wrap">{{ sampleMessage || '—' }}</div>
      </div>
      <div class="a2p-kv">
        <div class="k">Opt-in / opt-out</div>
        <div class="v" style="white-space: pre-wrap">{{ optInOut || '—' }}</div>
      </div>
    </div>

    <!-- Business verification -->
    <div class="a2p-section" style="margin-top: 32px">
      <div class="a2p-section-head">
        <div class="a2p-section-eyebrow">Pre-flight check</div>
        <h2>Verify against public records</h2>
        <p class="a2p-section-desc">
          Checks BBB and your website to confirm the business name and authorized
          representative match public records. Catches the rejection reasons before
          you submit.
        </p>
      </div>
      <button
        type="button"
        class="a2p-btn a2p-btn-secondary"
        @click="runVerification"
        :disabled="verifying"
      >
        {{ verifying ? 'Checking public records…' : 'Verify business & representative' }}
      </button>

      <template v-if="verificationResult">
        <A2PCallout
          v-if="verificationResult.matches && verificationResult.matches.length"
          type="success"
        >
          <div v-for="(m, i) in verificationResult.matches" :key="'m-' + i">✓ {{ m }}</div>
        </A2PCallout>

        <A2PCallout
          v-for="(w, i) in verificationResult.warnings"
          :key="'w-' + i"
          :type="w.severity === 'high' ? 'danger' : 'warn'"
        >
          <div><strong>{{ w.message }}</strong></div>
          <div v-if="w.submitted" style="margin-top: 4px">You entered: <strong>{{ w.submitted }}</strong></div>
          <div v-if="w.publicRecord">Public record shows: <strong>{{ w.publicRecord }}</strong></div>
          <div v-if="w.suggestion" style="margin-top: 4px">{{ w.suggestion }}</div>
          <div v-if="w.sourceUrl" style="margin-top: 4px; font-size: 12px;">
            Source: <a :href="w.sourceUrl" target="_blank" rel="noopener">{{ w.source }}</a>
          </div>
        </A2PCallout>

        <A2PCallout v-if="verificationResult.verificationStatus === 'pass'" type="success">
          <strong>Verification passed.</strong> Business name and representative match public records.
        </A2PCallout>
        <A2PCallout v-else-if="verificationResult.verificationStatus === 'fail'" type="danger">
          <strong>Potential mismatches found.</strong> Go back and correct the information, or
          the registration will likely be rejected.
        </A2PCallout>
      </template>

      <A2PCallout v-if="verificationError" type="warn">
        Verification check failed: {{ verificationError }}. You can still submit, but
        consider verifying manually.
      </A2PCallout>
    </div>

    <div class="a2p-actions">
      <button type="button" class="a2p-btn a2p-btn-secondary" @click="$emit('back')" :disabled="submitting">
        Back
      </button>
      <button
        type="button"
        class="a2p-btn a2p-btn-primary"
        @click="$emit('next')"
        :disabled="submitting || submitBlocked"
      >
        {{ submitting ? 'Submitting…' : 'Submit registration' }}
      </button>
    </div>
  </div>
</template>

<script>
import A2PCallout from './components/A2PCallout.vue';

export default {
  name: 'A2PWizardReview',
  components: { A2PCallout },
  props: [
    'eyebrow',
    'businessName', 'businessAddress', 'ein', 'businessType', 'website',
    'repName', 'repTitle', 'repEmail', 'repPhone',
    'brandDescription', 'sampleMessage', 'optInOut', 'submitting',
    'siteID', 'state',
  ],
  data() {
    return {
      verifying: false,
      verificationResult: null,
      verificationError: null,
    };
  },
  computed: {
    submitBlocked() {
      return this.verificationResult && this.verificationResult.verificationStatus === 'fail';
    },
  },
  methods: {
    async runVerification() {
      this.verifying = true;
      this.verificationResult = null;
      this.verificationError = null;
      try {
        const result = await this.$store.dispatch('a2p/verifyBusiness', {
          siteID: this.siteID,
          businessName: this.businessName,
          repName: this.repName,
          website: this.website,
          state: this.state,
        });
        if (result && result.success === false) {
          this.verificationError = result.error || 'Unknown error';
        } else {
          this.verificationResult = result;
        }
      } catch (err) {
        console.error('Business verification error:', err);
        this.verificationError = err.message || 'Unknown error';
      } finally {
        this.verifying = false;
      }
    },
  },
};
</script>

<style src="./a2p-wizard.css"></style>

<style scoped>
.a2p-review-section {
  margin: 24px 0;
  padding: 20px;
  background: var(--a2p-surface);
  border: 1px solid var(--a2p-border);
  border-radius: var(--a2p-radius-m);
}
.a2p-review-section .a2p-section-eyebrow {
  margin-bottom: 10px;
}
</style>
